import React from 'react';
import { ActivityItem, Icon, IconButton, Spinner, SpinnerSize, Stack } from '@fluentui/react';
import { useGetProcurementOrderActivitiesQuery } from '../../../../store/endpoints/ProcurementEndpoints';
import { IProcurementActivity } from '../../../../models/Procurement';
import { PROCUREMENT_ACTIVITY_TYPE, ProcurementActivityDisplay } from '../../../../Constants';

interface IOrderActivitiesProps {
    orderId: string;
}

const OrderActivities: React.FC<IOrderActivitiesProps> = ({ orderId }) => {
    const { data, isFetching, refetch } = useGetProcurementOrderActivitiesQuery({ orderId });

    return (
        <Stack>
            <Stack horizontal verticalAlign='center'>
                <span style={{ fontWeight: 600, flexGrow: 1 }}>Activities</span>
                <IconButton
                    iconProps={{ iconName: 'Refresh' }}
                    title='Refresh'
                    disabled={isFetching}
                    onClick={refetch}
                />
            </Stack>
            {isFetching ? (
                <Spinner
                    label='Loading activities...'
                    size={SpinnerSize.large}
                    style={{ marginTop: '3em' }}
                    styles={{
                        circle: { width: '60px', height: '60px', borderWidth: '4px' }
                    }}
                />
            ) : !data?.items?.length ? (
                <p style={{ color: '#605e5c', marginTop: '1em' }}>No activities recorded for this order.</p>
            ) : (
                <Stack tokens={{ childrenGap: '1em' }} style={{ marginTop: '0.5em' }}>
                    {data.items.map(activity => (
                        <OrderActivityItem key={activity.id} activity={activity} />
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

interface IOrderActivityItemProps {
    activity: IProcurementActivity;
}

const activityIcon = (activityType?: string): string => {
    switch (activityType) {
        case PROCUREMENT_ACTIVITY_TYPE.ORDER_APPROVED:
        case PROCUREMENT_ACTIVITY_TYPE.ORDER_SUBMITTED:
            return 'Send';
        case PROCUREMENT_ACTIVITY_TYPE.ORDER_SHIPPED:
            return 'DeliveryTruck';
        case PROCUREMENT_ACTIVITY_TYPE.ORDER_DELIVERED:
            return 'Completed';
        case PROCUREMENT_ACTIVITY_TYPE.ORDER_CANCELLED:
        case PROCUREMENT_ACTIVITY_TYPE.ITEMS_CANCELLED:
            return 'Cancel';
        case PROCUREMENT_ACTIVITY_TYPE.ITEMS_CREATED:
            return 'ProductList';
        case PROCUREMENT_ACTIVITY_TYPE.ORDER_CREATED:
            return 'AddToShoppingList';
        case PROCUREMENT_ACTIVITY_TYPE.ORDER_UPDATED:
        case PROCUREMENT_ACTIVITY_TYPE.ITEMS_UPDATED:
            return 'Edit';
        case PROCUREMENT_ACTIVITY_TYPE.ORDER_REJECTED:
            return 'ErrorBadge';
        default:
            return 'Info';
    }
};

const OrderActivityItem: React.FC<IOrderActivityItemProps> = ({ activity }) => {
    const commentStyle: React.CSSProperties = { marginTop: '0.25em', marginBottom: '0.25em' };
    const header = activity.user ? `${activity.user} - ${activity.vendor}` : activity.vendor;

    const description = (
        <>
            <strong>{header}</strong>&nbsp;
            {ProcurementActivityDisplay(activity.activityType)}.
        </>
    );

    const comments: React.ReactNode[] = [];
    if (activity.message) {
        comments.push(<p key='msg' style={commentStyle}>{activity.message}</p>);
    }
    if (activity.changedFields?.length) {
        comments.push(
            <p key='fields' style={{ ...commentStyle, display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {activity.changedFields.map(f => (
                    <span key={f} style={{
                        background: '#f3f2f1',
                        border: '1px solid #d2d0ce',
                        borderRadius: '2px',
                        padding: '1px 6px',
                        fontSize: '12px',
                        fontFamily: 'inherit'
                    }}>{f}</span>
                ))}
            </p>
        );
    }

    return (
        <ActivityItem
            activityDescription={description}
            comments={comments.length ? comments : undefined}
            timeStamp={new Date(activity.timestamp).toLocaleString()}
            activityIcon={<Icon iconName={activityIcon(activity.activityType)} />}
        />
    );
};

export default OrderActivities;
